import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { hostname } from '../../utils';

interface LogoUploaderProps {
  title: string;
  description: string;
  logoType: 'horizontal' | 'horizontal_dark' | 'square' | 'favicon' | 'onboarding_hero';
  logoId?: string | null;
  logoUrl?: string | null;
  whitelabelId?: string;
  getToken: Function;
  onLogoChange: (logoId: string | null, logoUrl: string | null, logoType: string) => void;
  recommended?: string;
  aspectRatio?: number;
}

const LogoUploader: React.FC<LogoUploaderProps> = ({
  title,
  description,
  logoType,
  logoId,
  logoUrl,
  whitelabelId,
  getToken,
  onLogoChange,
  recommended = '',
  aspectRatio = 1,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image size should be less than 2MB');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const token = await getToken();
      
      // Create FormData and append the file
      const formData = new FormData();
      formData.append('image', file);
      formData.append('name', `${title} - ${logoType}`);
      formData.append('category', 'whitelabel-logo');
      formData.append('public', 'true');
      formData.append('companyId', whitelabelId || '');

      // Upload the image to platform-images only
      const uploadResponse = await axios.post(
        `${hostname}/platform-images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (uploadResponse.status === 201) {
        const imageData = uploadResponse.data;
        
        // Only update the frontend state - don't make direct backend calls to whitelabel
        // The actual saving will happen when the user clicks "Save Changes" in the dialog
        console.log('LogoUploader calling onLogoChange:', { logoType, imageId: imageData.id, imageUrl: imageData.url });
        onLogoChange(imageData.id, imageData.url, logoType);
      }
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setLoading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = () => {
    // Only update the frontend state - don't make direct backend calls to whitelabel
    // The actual saving will happen when the user clicks "Save Changes" in the dialog
    console.log('LogoUploader calling onLogoChange (remove):', { logoType });
    onLogoChange(null, null, logoType);
  };

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {description}
        </Typography>
        {recommended && (
          <Typography variant="caption" color="text.secondary">
            Recommended: {recommended}
          </Typography>
        )}
      </CardContent>
      
      {logoUrl ? (
        <Box sx={{ position: 'relative', pt: '56.25%', mx: 2, mb: 2, borderRadius: 1, overflow: 'hidden' }}>
          <CardMedia
            component="img"
            image={logoUrl}
            alt={`${title} logo`}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: logoType === 'horizontal' ? 'contain' : 'cover',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.03)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              display: 'flex',
              gap: 1,
              p: 1,
              backgroundColor: 'rgba(0,0,0,0.5)',
              borderBottomLeftRadius: 4,
            }}
          >
            <IconButton 
              size="small" 
              onClick={handleUploadClick} 
              disabled={loading}
              sx={{ color: 'white' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleRemoveLogo} 
              sx={{ color: 'white' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: 140,
            mx: 2,
            mb: 2,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
          }}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Button
                startIcon={<UploadIcon />}
                onClick={handleUploadClick}
                color="primary"
                variant="outlined"
                disabled={loading}
                sx={{ mb: 1 }}
              >
                Upload {title}
              </Button>
            </>
          )}
        </Box>
      )}
      
      {error && (
        <Typography variant="caption" color="error" sx={{ px: 2, pb: 2 }}>
          {error}
        </Typography>
      )}
      
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Card>
  );
};

export default LogoUploader;